<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const linkItem = defineSchema({
	label: $.text('Label'),
	link: $.text('Link'),
	external: $.select('External', [
		{ label: 'No', value: '' },
		{ label: 'Yes', value: 'true' },
	]),
})
const defaults = {
	donateLink: '/donate',
	donateLabel: 'Donate',
	searchLink: '/search',
	menus: [
		{
			label: 'Torah Portion',
			link: '/parashah/beshalach',
			feature: 'portion',
			columns: [
				{
					heading: 'The Five Books',
					items: [
						{ label: 'Genesis', link: '/book/genesis', external: '' },
						{ label: 'Exodus', link: '/book/exodus', external: '' },
						{ label: 'Leviticus', link: '/book/leviticus', external: '' },
						{ label: 'Numbers', link: '/book/numbers', external: '' },
						{ label: 'Deuteronomy', link: '/book/deuteronomy', external: '' },
					],
				},
			],
		},
		{
			label: 'Holidays',
			link: '/holidays',
			feature: '',
			columns: [
				{
					heading: '',
					items: [
						{ label: 'Rosh Hashanah', link: '/holidays/rosh-hashanah', external: '' },
						{ label: 'Yom Kippur', link: '/holidays/yom-kippur', external: '' },
						{ label: 'Sukkot', link: '/holidays/sukkot', external: '' },
						{ label: 'Simchat Torah', link: '/topic/simchat-torah', external: '' },
						{ label: 'Chanukah', link: '/holidays/chanukah', external: '' },
					],
				},
				{
					heading: '',
					items: [
						{ label: 'Purim', link: '/holidays/purim', external: '' },
						{ label: 'Passover', link: '/holidays/passover', external: '' },
						{ label: 'Shavuot', link: '/holidays/shavuot', external: '' },
						{ label: "Tisha B'Av", link: '/holidays/tisha-bav', external: '' },
						{ label: 'Shabbat', link: '/holidays/shabbat', external: '' },
					],
				},
				{
					heading: '',
					items: [
						{ label: "Yom Ha'atzmaut", link: '/topic/yom-haatzmaut', external: '' },
						{ label: 'Yom Yerushalayim', link: '/topic/jerusalem', external: '' },
						{ label: 'Yom HaZikaron', link: '/topic/yom-hazikaron', external: '' },
						{ label: 'Yom HaShoah', link: '/topic/yom-hashoah', external: '' },
					],
				},
			],
		},
		{
			label: 'Scholarship & Faith',
			link: '/categories/bible-criticism',
			feature: '',
			columns: [
				{
					heading: '',
					items: [
						{ label: 'Biblical Criticism', link: '/categories/bible-criticism', external: '' },
						{ label: 'Modern Faith', link: '/categories/modern-faith', external: '' },
						{ label: 'Morality and Ethics', link: '/categories/morality-and-ethics', external: '' },
						{ label: 'Archaeology', link: '/categories/archaeology', external: '' },
						{ label: 'Psalms תהלים', link: '/categories/psalms', external: '' },
					],
				},
				{
					heading: '',
					items: [
						{
							label: 'Medieval Interpretation',
							link: '/categories/medieval-interpretation',
							external: '',
						},
						{ label: 'Christianity', link: '/categories/christianity', external: '' },
						{ label: 'Series and Symposia', link: '/series', external: '' },
						{ label: 'Printable Readers', link: '/printable-readers', external: '' },
					],
				},
			],
		},
		{
			label: 'Current',
			link: '/search',
			feature: 'featured',
			columns: [
				{
					heading: '',
					items: [
						{ label: 'Latest Essays', link: '/latest-essays', external: '' },
						{ label: 'Israel-Hamas War', link: '/israel-hamas-war', external: '' },
						{ label: 'Blog', link: '/blog', external: '' },
						{ label: 'Op-ed', link: '/op-ed', external: '' },
						{ label: 'Interview', link: '/interview', external: '' },
					],
				},
			],
		},
		{
			label: 'More',
			link: '/topics',
			feature: '',
			columns: [
				{
					heading: '',
					items: [
						{ label: 'Browse Topics', link: '/topics', external: '' },
						{ label: 'Our Authors', link: '/authors', external: '' },
						{ label: 'Submissions', link: '/about?section=submissions', external: '' },
						{ label: 'About Us', link: '/about', external: '' },
					],
				},
				{
					heading: '',
					items: [
						{ label: 'Contact Us', link: '/contact', external: '' },
						{ label: 'TheGemara.com', link: 'https://www.thegemara.com/', external: 'true' },
						{ label: 'thetorah.co.il', link: 'https://thetorah.co.il/', external: 'true' },
					],
				},
			],
		},
	],
}
const schema = defineSchema({
	logo: $.text('Logo URL'),
	donateLink: $.text('Donate Link'),
	donateLabel: $.text('Donate Label'),
	searchLink: $.text('Search Link'),
	menus: $.array(
		'Menus',
		defineSchema({
			label: $.text('Label'),
			link: $.text('Link'),
			// A dropdown can hold up to three columns of links. Extra columns are
			// only rendered when they contain items — a single-column menu just fills
			// the first array.
			columns: $.array(
				'Columns',
				defineSchema({
					heading: $.text('Column Heading (optional)'),
					items: $.array('Items', linkItem, { collapsible: true }),
				}),
				{ collapsible: true },
			),
			// Optional special panel injected alongside the columns, matching the
			// live site: `portion` = this week's Torah portion; `featured` = a
			// featured post card.
			feature: $.select('Feature Panel', [
				{ label: 'None', value: '' },
				{ label: "This Week's Portion", value: 'portion' },
				{ label: 'Featured Post', value: 'featured' },
			]),
		}),
		{ collapsible: true },
	),
})
export const BlockNav = defineBlock({
	thumbnail: '/block-thumbs/BlockNav.svg',
	category: 'Layout',
	order: 240,
	name: 'BlockNav',
	label: 'Navigation',
	icon: 'menu',
	global: true,
	schema,
	defaults,
})
</script>
<script setup lang="ts">
import { useGlobalBlock, useSeo } from '@bagelink/blox'
import { useRoute } from 'vue-router'
import { useBoundPageItem } from '@/composables/useBoundPageItem'
import { sanitizeArticleTitleHtml } from '@/utils/articleHtml'
import { pageSeo } from '@/utils/seo'
import { AccordionItem, Btn, Dropdown, Icon, Card } from '@bagelink/vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useWeeklyParasha } from '@/composables/useWeeklyParasha'
import { useArticlesWithAuthors } from '@/composables/useDatastore'
import { useNewsletter } from '@/composables/useNewsletter'
import { useParashahSlugs } from '@/composables/useParashahSlugs'
import NewsletterDialog from '@/components/NewsletterDialog.vue'
import HebrewSearchInput from '@/components/HebrewSearchInput.vue'





interface MenuItem {
	label: string
	link: string
	external?: string
}

interface MenuColumn {
	heading?: string
	items: MenuItem[]
}

interface Menu {
	label: string
	link: string
	columns?: MenuColumn[]
	feature?: string
}

const props = defineProps<{
	donateLink?: string
	donateLabel?: string
	searchLink?: string
	menus?: Menu[]
}>()

const route = useRoute()
const seoRecord = useBoundPageItem()
useSeo(() => pageSeo(route.path, seoRecord.value))

// Global block: content is shared site-wide and edited once in the studio.
// Prefer the CMS-stored content, then props, then schema defaults.
const { data: remote } = useGlobalBlock<{
	donateLink?: string
	donateLabel?: string
	searchLink?: string
	menus?: Menu[]
}>('BlockNav')
const donateLink = computed(
	() => remote.value?.donateLink ?? props.donateLink ?? defaults.donateLink,
)
const donateLabel = computed(
	() => remote.value?.donateLabel ?? props.donateLabel ?? defaults.donateLabel,
)
const searchLink = computed(
	() => remote.value?.searchLink ?? props.searchLink ?? defaults.searchLink,
)

/** Keep previously saved global-navigation content compatible with canonical URLs. */
function canonicalLink(link = '') {
	if (link === '/search') return '/latest-essays'
	return link
		.replace(/^\/topic\/israel-hamas-war(?=$|[?#])/, '/israel-hamas-war')
		.replace(/^\/categories\/op-ed(?=$|[?#])/, '/op-ed')
		.replace(/^\/categories\/interview(?=$|[?#])/, '/interview')
		.replace(/^\/books\//, '/book/')
		.replace(/^\/holiday\//, '/holidays/')
		.replace(/^\/category\//, '/categories/')
		.replace(/^\/categories\/biblical-criticism(?=$|[?#])/, '/categories/bible-criticism')
		.replace(/^\/categories\/morality-ethics(?=$|[?#])/, '/categories/morality-and-ethics')
		.replace(/^\/submissions(?=$|[?#])/, '/about?section=submissions')
		.replace(/^\/about#submissions(?=$)/, '/about?section=submissions')
		// Five holidays have no page of their own — the live site sends these menu
		// items to the matching topic page, so any stored nav content follows suit.
		.replace(/^\/holidays\/simchat-torah(?=$|[?#])/, '/topic/simchat-torah')
		.replace(/^\/holidays\/yom-haatzmaut(?=$|[?#])/, '/topic/yom-haatzmaut')
		.replace(/^\/holidays\/yom-yerushalayim(?=$|[?#])/, '/topic/jerusalem')
		.replace(/^\/holidays\/yom-hazikaron(?=$|[?#])/, '/topic/yom-hazikaron')
		.replace(/^\/holidays\/yom-hashoah(?=$|[?#])/, '/topic/yom-hashoah')
}

function canonicalMenu(menu: Menu): Menu {
	const canonicalItems = (items?: MenuItem[]) =>
		items?.map(item => ({
			...item,
			link: item.external === 'true' ? item.link : canonicalLink(item.link),
		}))

	return {
		...menu,
		link: canonicalLink(menu.link),
		columns: menu.columns?.map(column => ({
			...column,
			items: canonicalItems(column.items) ?? [],
		})),
		...((menu as any).items ? { items: canonicalItems((menu as any).items) } : {}),
	}
}

const menus = computed<Menu[]>(() =>
	(remote.value?.menus?.length
		? remote.value.menus
		: props.menus?.length
			? props.menus
			: defaults.menus
	).map(canonicalMenu),
)

// ── Feature panels ──────────────────────────────────────────────────────────
// "This Week's Torah Portion" column (live from the Hebrew calendar) and the
// "Featured Post" card, matching the original thetorah.com dropdowns.
const { primary: weeklyPortion, reference: weeklyReference } = useWeeklyParasha()
const { pathFor: parashahLink } = useParashahSlugs()
const weeklyPortionLink = computed(() =>
	weeklyPortion.value?.name ? parashahLink(weeklyPortion.value.name) : '',
)

// One newest-featured article for the "Current" dropdown's Featured Post panel.
const { articles: featuredArticles } = useArticlesWithAuthors({ limit: 12 })
const featuredPost = computed(() => {
	const list = featuredArticles.value
	if (!list.length) return null
	const flagged = list.find((a: any) => a.featuredHomepage)
	return flagged ?? list[0]
})

const FEATURED_POST_SOURCE_BY_ID: Record<string, string> = {
	'5cee70f48e610a3d07c75e7d': 'Op-Ed',
	'5cee70f48e610adba3c75e7e': 'Interview',
	'5cee70f48e610a27a1c75ecf': 'Blog',
}

const featuredPostSource = computed(() => {
	const post = featuredPost.value as any
	if (!post) return ''
	if (post.blogTypeId && FEATURED_POST_SOURCE_BY_ID[post.blogTypeId]) {
		return FEATURED_POST_SOURCE_BY_ID[post.blogTypeId]
	}
	if (post.type === 'op-ed' || post.link?.startsWith('/op-ed/')) return 'Op-Ed'
	if (post.type === 'interview' || post.link?.startsWith('/interview/')) return 'Interview'
	if (post.type === 'blog' || post.link?.startsWith('/blog/')) return 'Blog'
	return ''
})

// Every dropdown's columns, always as an array (a bare `items` legacy menu is
// wrapped into a single column so the template can iterate uniformly).
function columnsOf(menu: Menu): MenuColumn[] {
	if (menu.columns?.length) return menu.columns
	const legacy = (menu as any).items as MenuItem[] | undefined
	return legacy?.length ? [{ items: legacy }] : []
}

// Flat item list for the mobile accordion (columns don't apply on mobile).
function flatItems(menu: Menu): MenuItem[] {
	return columnsOf(menu).flatMap(c => c.items ?? [])
}

const scrolledMenu = ref(false)
const openMobileMenu = ref(false)
const windowWidth = ref(1024)

const scrollThresholdDown = 180
const scrollThresholdUp = 60

const isDesktop = computed(() => windowWidth.value > 910)

function handleScroll() {
	if (windowWidth.value > 910) {
		if (window.scrollY > scrollThresholdDown && !scrolledMenu.value) {
			scrolledMenu.value = true
		} else if (window.scrollY < scrollThresholdUp && scrolledMenu.value) {
			scrolledMenu.value = false
		}
	} else {
		scrolledMenu.value = false
	}
}

function handleResize() {
	windowWidth.value = window.innerWidth
	if (windowWidth.value <= 910) {
		scrolledMenu.value = false
	}
}

onMounted(() => {
	windowWidth.value = window.innerWidth
	window.addEventListener('scroll', handleScroll)
	window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
	window.removeEventListener('scroll', handleScroll)
	window.removeEventListener('resize', handleResize)
})

// ── Newsletter popup ────────────────────────────────────────────────────────
// Matches the live site: the "Newsletter" action opens a subscribe dialog
// rather than navigating to a page. The dialog + submit logic are shared (so
// the footer's "Subscribe" row opens the same popup) — see `useNewsletter`.
const { openNewsletter } = useNewsletter()
</script>

<template>
	<nav
		class="border-bottom pt-1-5 pb-075 m_sticky-0 m_bg-white m_p-075 z-999 bg-white main-nav"
		:class="{ 'scrolledMenu pt-025': scrolledMenu, 'pb-1-5': !scrolledMenu }"
	>
		<div
			class="relative txt-center w1100px hideOnScroll pb-1 m_pb-0 m_flex m_space-between m_align-items-center"
		>
			<div class="absolute start top m_none">
				<Btn :to="donateLink" thin flat class="color-primary" :value="donateLabel" />
				<Btn thin flat class="color-gray-70" value="Newsletter" @click="openNewsletter" />
			</div>
			<RouterLink to="/" class="inline-block m_w-170px">
				<img src="@/assets/logo-thetorah.svg" alt="the torah logo" />
			</RouterLink>
			<HebrewSearchInput :search-path="searchLink" class="w240px absolute end top m_none" />
			<Btn class="none m_block color-red px-075" round flat @click="openMobileMenu = true">
				<img src="@/assets/burger.svg" alt="burger" />
			</Btn>
		</div>
		<div
			class="flex gap-1 justify-content-center relative w1100px pt-05 m_column mobileMenu m_h100vh m_fixed m_top m_end m_bg-gray m_align-items-start m_justify-content-start m_w280px z-9 m_block"
			:class="{ openMobileMenu }"
		>
			<RouterLink to="/" class="w130px ShowOnScroll">
				<img src="@/assets/logo-thetorah.svg" alt="the torah logo" />
			</RouterLink>
			<div
				class="none m_flex m_space-between m_w-100p p-05 m_justify-content-center m_align-items-center gap-05"
			>
				<HebrewSearchInput
					:search-path="searchLink"
					class="bg-input-white none m_flex m_mb-0 flex-1"
				/>
				<Btn flat thin round icon="close" class="color-red" @click="openMobileMenu = false" />
			</div>
			<!-- Menu dropdowns -->
			<template v-for="menu in menus" :key="menu.label">
				<Dropdown
					v-if="isDesktop"
					:value="menu.label"
					:triggers="['hover']"
					show-group="thetorah-navigation"
					:delay="{ show: 0, hide: 250 }"
				>
					<Card class="display-flex overflow-hidden radius-1 nav-dropdown bg-gray-40 p-0">
						<!-- This week's Torah portion panel (live from the Hebrew calendar) -->
						<RouterLink
							v-if="menu.feature === 'portion' && weeklyPortion && weeklyPortionLink"
							:to="weeklyPortionLink"
							class="p-1 decoration-none min-w-240px hover-opacity-9 transition"
						>
							<p class="txt13 tracking-wide opacity-6 pb-075 m-0">This Week's Torah Portion</p>
							<div class="flex gap-05 flex-wrap w-230px">
								<p class="txt-24 semi line-height-1 m-0">
									{{ weeklyPortion.name }}
								</p>
								<p class="txt-27 line-height-11">
									{{ weeklyPortion.nameHebrew }}
								</p>
							</div>
							<p class="txt12 m-0 flex align-items-center gap-025 pt-1 italic opacity-6">
								{{ weeklyReference }}
								<!-- <Icon name="arrow_forward" class="txt14" /> -->
							</p>
						</RouterLink>

						<!-- Link columns -->
						<div
							v-for="(col, i) in columnsOf(menu)"
							:key="i"
							class="py-1 bg-gray-40 px-1 flex column gap-05 min-w-190px"
							:class="{ 'border-start bg-gray-90': i === columnsOf(menu).length - 1 }"
						>
							<Btn
								v-for="item in col.items"
								:key="item.link"
								:to="item.external === 'true' ? undefined : item.link"
								:href="item.external === 'true' ? item.link : undefined"
								thin
								flat
								class="block radius-05 nav-item hover-opacity-7"
								align-txt="start"
								:value="item.label"
							/>
						</div>

						<!-- Featured Post panel -->
						<RouterLink
							v-if="menu.feature === 'featured' && featuredPost"
							:to="featuredPost.link"
							class="p-1-5 bg-gray-90 decoration-none block w240px border-start hover-opacity-9 transition"
						>
							<p class="txt11 uppercase tracking-wide color-gray-70 semi pb-075 m-0 italic">
								Featured Post
							</p>

						<p class="txt14 semi line-height-17 color-black m-0" v-html="sanitizeArticleTitleHtml(featuredPost.title)" />
							<p v-if="featuredPostSource" class="txt14 semi color-red m-0">
								{{ featuredPostSource }}
							</p>
						</RouterLink>
					</Card>
				</Dropdown>
				<AccordionItem v-else :label="menu.label" class="AccordionItemMobile">
					<div class="p-1 bg-gray-20 overflow max-h-400px border-top border-bottom m_p-0">
						<RouterLink
							v-if="menu.feature === 'portion' && weeklyPortion && weeklyPortionLink"
							:to="weeklyPortionLink"
							class="block p-1 mb-05 decoration-none bg-gray-40 radius-1 txt-center border-bottom"
							@click="openMobileMenu = false"
						>
							<p class="txt12 tracking-wide opacity-6 pb-05 m-0">This Week's Torah Portion</p>
							<div class="flex gap-05 flex-wrap justify-content-center w190px mx-auto">
								<p class="txt24 semi line-height-1 m-0">
									{{ weeklyPortion.name }}
								</p>
								<p class="txt27 line-height-11 m-0" dir="rtl">
									{{ weeklyPortion.nameHebrew }}
								</p>
							</div>
							<p class="txt12 m-0 pt-05 italic opacity-6">
								{{ weeklyReference }}
							</p>
						</RouterLink>
						<div class="m_p-1">
							<Btn
								v-for="item in flatItems(menu)"
								:key="item.link"
								:to="item.external === 'true' ? undefined : item.link"
								:href="item.external === 'true' ? item.link : undefined"
								thin
								flat
								class="block py-1"
								align-txt="start"
								:value="item.label"
								@click="openMobileMenu = false"
							/>
						</div>
					</div>
				</AccordionItem>
			</template>
			<HebrewSearchInput :search-path="searchLink" class="w240px ShowOnScroll mb-0 ms-auto" />
			<div
				class="absolute end flex hideOnScroll m_relative m_column m_gap-1 m m_align-items-start m_pt-1 m_px-075"
			>
				<Btn
					:to="donateLink"
					thin
					flat
					class="none m_block color-primary txt12"
					:value="donateLabel"
				/>
				<Btn
					thin
					flat
					class="none m_block color-primary txt12"
					value="Newsletter"
					@click="openNewsletter"
				/>
				<Btn to="/store/chumash-bereshit" thin flat class="txt12 m_txt14 color-red">
					<Icon name="shopping_cart" class="txt14 rotateY-180" />
					<p>חומש</p>
				</Btn>
				<Btn href="https://thetorah.co.il/" thin flat class="txt12 m_txt14" value="עברית" />
			</div>
		</div>
		<div
			class="none m_block m_inset m_fixed m_z-1 mobileMenu"
			:class="{ openMobileMenu }"
			@click="openMobileMenu = false"
		/>

		<!-- Newsletter subscribe popup (opened from the nav + footer "Newsletter" links) -->
		<NewsletterDialog />
	</nav>
</template>

<style>
/* Dropdown link rows: subtle hover fill so items feel clickable (like the live site). */

.scrolledMenu {
	position: sticky;
	top: 0;
	z-index: 999;
	animation: openMenu 0.2s ease-in-out;
}
.ShowOnScroll {
	display: none !important;
}
@keyframes openMenu {
	from {
		transform: translateY(-100%);
	}
	to {
		transform: translateY(0%);
	}
}
.scrolledMenu .hideOnScroll {
	display: none;
}
.scrolledMenu .ShowOnScroll {
	display: block !important;
}

@media screen and (max-width: 910px) {
	.mobileMenu {
		transform: translateX(105%);
		transition: var(--bgl-transition);
	}
	.mobileMenu.m_w280px.openMobileMenu {
		box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.2);
	}
	.openMobileMenu {
		transform: translateX(0%);
		transition: var(--bgl-transition);
	}
	.AccordionItemMobile {
		width: 100% !important;
		font-weight: 300 !important;
		padding: 0 !important;
	}
	.AccordionItemMobile .accordion-head {
		padding: 0rem 1rem !important;
		height: 50px !important;
	}
	.AccordionItemMobile .accordion-label {
		font-weight: 300 !important;
		font-size: 15px;
	}
	.AccordionItemMobile .accordion-head:hover .accordion-label {
		text-decoration: none !important;
	}
	.AccordionItemMobile [aria-expanded='true'].accordion-head {
		color: var(--bgl-primary) !important;
	}
}
</style>
